"use client";

import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { ChatWidget, RollWidget, RollWidgetFrame, type ChatMessage } from "@/components/roll-widgets";
import { useLiveSession } from "@/contexts/live-session-context";
import type { HallMessage, LiveHall } from "@/lib/live/hall";
import type { LiveParticipantLine, LiveParticipantsReply } from "@/lib/live/session-control";
import { useLiveParticipants } from "@/lib/live/use-live-participants";
import { PauseIcon, PlayIcon } from "../ui/atoms/icons";
import { LiveDiagnostics } from "./live-diagnostics";
import { LatencyReadout, verdictColor } from "./live-session-panel";
import { closeLobby, openLobby, useLobbyOpen } from "./lobby";
import { PeopleList, sortAudience } from "./recital-audience";
import { ShareInvite } from "./share-invite";

/** Un nom et sa latence tiennent sur une ligne, et le code en grand aussi. */
const JAM_WIDTH_PX = 268;
const STATS_WIDTH_PX = 300;

/**
 * Le relevé des participants hors du fournisseur live : rien à demander.
 * Déclaré une fois, pour que le relevé ne se relance pas à chaque rendu.
 */
async function noParticipants(): Promise<LiveParticipantsReply | null> {
  return null;
}

/**
 * La séance où l'on joue à plusieurs, sur le rouleau.
 *
 * # Pas de bandeau
 *
 * Tout ce que le bandeau de séance montrait (`LiveSessionPanel`) est ici,
 * décidé le 2026-09-23 : il recouvrait le rouleau au moment où l'on joue, et
 * s'ouvrait par un indicateur de plus dans la barre. Trois widgets à la place :
 *
 * - **la séance**, en haut à droite, là où « Inviter » a été cliqué : qui est
 *   là et sa latence, la pause, le code et le lien, et la sortie ;
 * - **les statistiques**, en haut à gauche, ouvertes depuis la séance : le
 *   relevé détaillé de chaque liaison, pour quand ça « traîne ». À gauche et
 *   non sous la séance : le rouleau n'a pas la hauteur de deux panneaux
 *   empilés, et celui du dessous recouvrait le code et la sortie ;
 * - **le chat**, en bas à gauche, replié en bulle : tout le monde joue, et un
 *   fil ouvert par-dessus le rouleau se lit entre deux morceaux. Pas sous la
 *   séance : déplié, il recouvrait la confirmation de « Terminer », qui fait
 *   grandir le coin vers le bas. Il ne peut chevaucher que les statistiques,
 *   ouvertes de loin en loin, et on le tire d'un coin à l'autre.
 *
 * Le régime (tout le monde joue, ou une personne mène) n'a plus de commande :
 * une séance ouverte par « Inviter » naît en Jam, et c'est le seul régime qui
 * a un sens quand on invite quelqu'un à jouer.
 *
 * # Le coin s'ouvre et se range
 *
 * Le picto de la barre ouvre et ferme la séance, sa croix la range aussi
 * (`lobby.tsx` tient l'état, partagé avec « Seul ou à plusieurs »). Elle
 * s'ouvre d'elle-même en entrant — par un lien reçu, c'est la seule façon de
 * savoir qui est là —, et se referme quand la séance finit. Le chat, lui, ne
 * dépend pas du coin : on le réduit en bulle.
 */
export function JamWidgets() {
  const live = useLiveSession();
  const open = useLobbyOpen();
  const [statsOpen, setStatsOpen] = useState(false);

  const session = live?.session ?? null;
  const together = session !== null && session.recital === null && live?.role === "player";
  const inRecital = session?.recital != null;
  const room = live?.room ?? null;

  // Entrer dans une séance à plusieurs ouvre son widget, une fois par salle :
  // le refermer ensuite est un choix qui tient. Un récital a sa console à lui,
  // et le widget du coin se ferme.
  useEffect(() => {
    if (together) {
      openLobby();
    } else if (inRecital) {
      closeLobby();
    }
  }, [inRecital, room, together]);

  // Monté le temps d'une séance (`StudioPage`) : la séance finie, rien ne
  // reste ouvert pour la suite.
  useEffect(() => () => closeLobby(), []);

  // Un seul relevé pour les deux widgets qui en vivent, et seulement quand
  // l'un d'eux est ouvert. Une séance à plusieurs compte quelques personnes :
  // une requête toutes les trois secondes chacune reste sans conséquence.
  const lines = useLiveParticipants(
    live?.fetchParticipants ?? noParticipants,
    room,
    together && (open || statsOpen),
  );

  if (live === null || !together) {
    return null;
  }

  return (
    <>
      {open && <SessionCorner lines={lines?.participants ?? null} onOpenStats={() => setStatsOpen(true)} />}
      {statsOpen && <SessionStats lines={lines?.participants ?? null} onClose={() => setStatsOpen(false)} />}
      {live.hall !== null && <SessionChat hall={live.hall} />}
    </>
  );
}

/**
 * La latence d'un autre, en un nombre : ce que ses notes mettent à nous
 * parvenir, mesuré (`transitMs`, dès que des notes ont circulé), ou la moitié
 * de sa liaison d'ici là. `null` tant que le service n'a rien de lui.
 *
 * Pas « je l'entends » : en Jam, ce nombre-là vaut le délai commun pour tout
 * le monde, et il est déjà dans l'en-tête. Ce qui distingue les participants,
 * c'est ce que chacun coûte au réseau — c'est lui qu'on regarde quand l'un
 * d'eux traîne.
 */
function latencyOf(line: LiveParticipantLine): number | null {
  const measures = line.measures;

  if (measures === null) {
    return null;
  }

  return measures.transitMs > 0 ? measures.transitMs : measures.legMs / 2;
}

/**
 * Le coin de la séance : qui est là, la pause, le code et le lien, la sortie.
 *
 * # Partir
 *
 * Qui a ouvert la séance la ferme en partant, et tout le monde en sort : c'est
 * le même chemin que l'arrêt d'un récital (`leave` → fermeture → salle
 * supprimée). D'où deux temps pour lui, et un seul pour les autres, qui ne
 * ferment rien derrière eux.
 */
function SessionCorner({
  lines,
  onOpenStats,
}: {
  lines: readonly LiveParticipantLine[] | null;
  onOpenStats: () => void;
}) {
  const live = useLiveSession();
  const [armed, setArmed] = useState(false);

  if (live === null) {
    return null;
  }

  const mine = live.session?.mine === true;
  const code = live.session?.code ?? null;
  const paused = live.sessionState?.paused ?? null;

  return (
    <RollWidget anchor="top-right">
      <RollWidgetFrame
        kicker="À plusieurs"
        width={JAM_WIDTH_PX}
        ariaLabel="La séance à plusieurs"
        onClose={closeLobby}
        trailing={
          paused === true ? (
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] text-amber-300">
              <span aria-hidden className="size-1.5 rounded-full bg-amber-400" />
              en pause
            </span>
          ) : live.jam && live.delayMs > 0 ? (
            <span className="shrink-0 text-[11px] tabular-nums text-text-muted" title="Le délai que tout le monde partage">
              +{Math.round(live.delayMs)}&nbsp;ms
            </span>
          ) : undefined
        }
        footer={
          mine ? (
            <EndButton armed={armed} onArm={setArmed} onEnd={live.leave} />
          ) : (
            <button
              type="button"
              onClick={() => void live.leave()}
              className="h-7 w-full cursor-pointer rounded-md text-[12px] font-semibold text-text-secondary transition-colors duration-150 hover:bg-white/[0.06] hover:text-text-primary"
            >
              Quitter
            </button>
          )
        }
      >
        <div className="flex flex-col gap-3 px-4 py-3">
          <Players identity={live.identity} lines={lines} />

          <div className="flex items-center justify-between gap-3">
            <PauseButton paused={paused} onChange={live.setTransportPaused} />

            <button
              type="button"
              onClick={onOpenStats}
              className="cursor-pointer rounded text-[12px] text-text-muted transition-colors duration-150 hover:text-text-primary"
            >
              Statistiques
            </button>
          </div>

          {live.startFailure !== null && (
            <p role="alert" className="m-0 text-[11px] leading-snug text-red-300">
              {live.startFailure}
            </p>
          )}

          <div className="border-t border-border-subtle pt-3">
            <ShareInvite code={code} shareUrl={live.shareUrl} copyShareUrl={live.copyShareUrl} />
          </div>
        </div>
      </RollWidgetFrame>
    </RollWidget>
  );
}

/**
 * Les autres joueurs, au dessin de « La salle » d'un récital (`PeopleList`),
 * avec la latence de chacun à droite du nom.
 *
 * Pas de ligne pour soi, comme dans « La salle » : c'est soi, et ce qu'elle
 * dirait se lit déjà dans la barre. Les noms viennent du relevé du service, qui
 * porte aussi les mesures ; en attendant son premier passage, de LiveKit, qui
 * les connaît dès la jointure — tout le monde y est visible quand tout le
 * monde joue, donc la liste ne passe jamais par le squelette.
 */
function Players({
  identity,
  lines,
}: {
  identity: string | null;
  lines: readonly LiveParticipantLine[] | null;
}) {
  const live = useLiveSession();
  const participants = live?.participants;

  const others = useMemo(() => {
    const source: readonly LiveParticipantLine[] =
      lines ??
      (participants ?? []).map((participant) => ({
        identity: participant.id,
        name: participant.name,
        canPlay: true,
        measures: null,
        muted: false,
      }));

    return sortAudience(
      source.filter((line) => line.identity !== identity),
      identity,
    );
  }, [identity, lines, participants]);

  return (
    <div className="flex flex-col">
      <PeopleList
        identity={identity}
        people={others}
        empty="Personne d'autre pour l'instant. Envoie le code ou le lien."
        trailing={(line) => {
          const latency = latencyOf(line);

          return (
            <span
              className={`shrink-0 text-[12px] tabular-nums ${latency === null ? "text-text-muted" : verdictColor(latency)}`}
              title="Ce que ses notes mettent à te parvenir"
            >
              {latency === null ? "—" : `${Math.round(latency)} ms`}
            </span>
          );
        }}
      />
    </div>
  );
}

/**
 * Mettre la séance en pause, ou la reprendre — pour tout le monde.
 *
 * Le geste va au service, seul arbitre de la position partagée, et revient à
 * chacun par l'état publié : rien ne s'applique ici en attendant. `paused` à
 * `null` veut dire qu'aucun état n'est encore lu, et le bouton se retire
 * plutôt que de mentir sur le sens de son clic.
 */
function PauseButton({
  paused,
  onChange,
}: {
  paused: boolean | null;
  onChange: (paused: boolean) => Promise<boolean>;
}) {
  const [busy, setBusy] = useState(false);

  if (paused === null) {
    return <span />;
  }

  const toggle = async () => {
    setBusy(true);

    try {
      await onChange(!paused);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={() => void toggle()}
      disabled={busy}
      className="inline-flex cursor-pointer items-center gap-1.5 rounded text-[12px] font-medium text-text-secondary transition-colors duration-150 hover:text-text-primary disabled:cursor-wait disabled:opacity-60"
    >
      {paused ? <PlayIcon size={13} /> : <PauseIcon size={13} />}
      {paused ? "Reprendre" : "Mettre en pause"}
    </button>
  );
}

/** Terminer, en deux temps : tout le monde sort, et la séance ne rouvrira pas. */
function EndButton({
  armed,
  onArm,
  onEnd,
}: {
  armed: boolean;
  onArm: (armed: boolean) => void;
  onEnd: () => Promise<void>;
}) {
  if (!armed) {
    return (
      <button
        type="button"
        onClick={() => onArm(true)}
        className="h-7 w-full cursor-pointer rounded-md text-[12px] font-semibold text-text-secondary transition-colors duration-150 hover:bg-white/[0.06] hover:text-text-primary"
      >
        Terminer la séance
      </button>
    );
  }

  return (
    <div className="flex w-full flex-col gap-2">
      <p className="m-0 text-[12px] leading-[1.45] text-text-secondary">
        Terminer la séance&nbsp;? Tout le monde en sort, et le code ne mènera plus nulle part.
      </p>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void onEnd()}
          className="h-7 shrink-0 cursor-pointer rounded-md bg-red-500/10 px-2.5 text-[12px] font-semibold text-red-300 transition-colors duration-150 hover:bg-red-500/15"
        >
          Oui, terminer
        </button>

        <button
          type="button"
          onClick={() => onArm(false)}
          className="h-7 shrink-0 cursor-pointer rounded-md px-2.5 text-[12px] text-text-secondary transition-colors duration-150 hover:bg-white/[0.06]"
        >
          Non
        </button>
      </div>
    </div>
  );
}

/**
 * Les statistiques de la séance : chaque liaison en détail.
 *
 * C'est le relevé que montrait le bandeau (`LatencyReadout`) — les deux
 * moitiés de chaque trajet, ce que les notes ont vraiment coûté, le délai, la
 * sortie audio —, et les diagnostics dépliables en dessous. Rien de tout ça ne
 * sert à jouer ; tout sert à savoir à qui s'adresser quand ça traîne.
 */
function SessionStats({
  lines,
  onClose,
}: {
  lines: readonly LiveParticipantLine[] | null;
  onClose: () => void;
}) {
  const live = useLiveSession();
  const [diagnostics, setDiagnostics] = useState(false);

  if (live === null) {
    return null;
  }

  return (
    <RollWidget anchor="top-left">
      <RollWidgetFrame kicker="Statistiques" width={STATS_WIDTH_PX} ariaLabel="Statistiques de la séance" onClose={onClose}>
        <div
          className="flex flex-col gap-3 overflow-y-auto px-4 py-3"
          style={{
            // Borné au rouleau, et assez court pour laisser la bulle du chat
            // en bas du même côté : un relevé à trois participants
            // descendrait jusqu'au clavier.
            maxHeight: "min(440px, 58cqh)",
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(255,255,255,0.12) transparent",
          }}
        >
          <LatencyReadout
            identity={live.identity}
            lines={lines}
            jam={live.jam}
            spectator={false}
            localLatency={live.localLatency}
            lead={live.lead}
            emitting={live.emitting}
            delayMs={live.delayMs}
          />

          <details
            className="group rounded-lg border border-dashed border-border-subtle"
            onToggle={(event) => setDiagnostics(event.currentTarget.open)}
          >
            <summary className="flex cursor-pointer select-none items-center justify-between px-3 py-2 text-[12px] text-text-muted hover:text-text-secondary [&::-webkit-details-marker]:hidden">
              Diagnostics
              <span aria-hidden="true" className="transition-transform duration-150 group-open:rotate-90">
                ›
              </span>
            </summary>

            {diagnostics && (
              <div className="flex flex-col gap-3 px-3 pb-3">
                <LiveDiagnostics active={diagnostics} />
              </div>
            )}
          </details>
        </div>
      </RollWidgetFrame>
    </RollWidget>
  );
}

const NO_MESSAGES: readonly HallMessage[] = [];

/**
 * Le fil de la séance, en bas à gauche.
 *
 * La salle de la séance (`LiveHall`) est celle des récitals : le service Go la
 * tient pour toute séance, et ne garde rien — un rechargement repart de zéro.
 * Tout le monde joue, donc personne ne porte d'étiquette et personne ne
 * modère : on se connaît. Replié en bulle par défaut, comme chez l'interprète
 * d'un récital et pour la même raison — on regarde son rouleau en jouant, et la
 * bulle compte ce qu'on n'a pas lu.
 */
function SessionChat({ hall }: { hall: LiveHall }) {
  const live = useLiveSession();

  const subscribe = useMemo(() => (listener: () => void) => hall.subscribe(listener), [hall]);
  const snapshot = useMemo(() => () => hall.snapshot(), [hall]);
  const held = useSyncExternalStore(subscribe, snapshot, () => NO_MESSAGES);

  const messages = useMemo<readonly ChatMessage[]>(
    () =>
      held.map((message) => ({
        id: message.id,
        at: message.at,
        text: message.text,
        author: { id: message.from.identity, name: message.from.name, role: "player" },
      })),
    [held],
  );

  const send = useCallback((text: string) => hall.say(text), [hall]);

  if (live === null) {
    return null;
  }

  return (
    <ChatWidget
      messages={messages}
      selfId={live.identity ?? ""}
      onSend={send}
      audience={live.participants.length + 1}
      collapsedByDefault
      ariaLabel="Discussion de la séance"
    />
  );
}
